import type { AssinaturaUploadDTO } from "@caint/shared";
import { useCallback, useEffect, useState } from "react";
import { type AssinaturaView, apiClient } from "./api.ts";
import { mensagemAmigavel } from "./erros.ts";

type ContentTypeAssinatura = AssinaturaUploadDTO["contentType"];
const TIPOS_OK: readonly string[] = ["image/png", "image/jpeg", "image/webp"];
const LIMITE_BYTES = 2_000_000;

export interface AssinaturaForm {
  atual: AssinaturaView | null;
  carregando: boolean;
  enviando: boolean;
  erro: string | null;
  sucesso: boolean;
  enviar: (arquivo: File) => Promise<void>;
  remover: () => Promise<void>;
}

/** SHA-256 (hex) do arquivo — integridade da assinatura (gravada no servidor). */
async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Lógica de "Minha assinatura" (upload da imagem para o B2 via presign→PUT→
 * confirmar, + preview/remover), compartilhada por desktop e mobile. Valida tipo/
 * tamanho no cliente e calcula o SHA-256 para integridade.
 */
export function useAssinatura(token: string | null): AssinaturaForm {
  const [atual, setAtual] = useState<AssinaturaView | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);

  const carregar = useCallback(async () => {
    if (!token) {
      return;
    }
    setCarregando(true);
    try {
      setAtual(await apiClient.obterAssinatura(token));
    } catch {
      // silencioso: sem assinatura ou storage indisponível — a UI mostra "nenhuma".
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const enviar = useCallback(
    async (arquivo: File) => {
      setErro(null);
      setSucesso(false);
      if (!token) {
        return;
      }
      if (!TIPOS_OK.includes(arquivo.type)) {
        setErro("Envie uma imagem PNG, JPG ou WEBP (PNG com fundo transparente é o ideal).");
        return;
      }
      if (arquivo.size > LIMITE_BYTES) {
        setErro("A imagem excede 2 MB.");
        return;
      }
      const contentType = arquivo.type as ContentTypeAssinatura; // validado acima
      setEnviando(true);
      try {
        const buffer = await arquivo.arrayBuffer();
        const sha256 = await sha256Hex(buffer);
        const prep = await apiClient.presignAssinatura({ contentType, tamanhoBytes: arquivo.size }, token);
        await apiClient.enviarArquivo(prep.uploadUrl, arquivo, contentType);
        await apiClient.confirmarAssinatura(
          { contentType, tamanhoBytes: arquivo.size, id: prep.id, chaveB2: prep.chaveB2, sha256 },
          token,
        );
        setSucesso(true);
        await carregar();
      } catch (e) {
        setErro(mensagemAmigavel(e));
      } finally {
        setEnviando(false);
      }
    },
    [token, carregar],
  );

  const remover = useCallback(async () => {
    setErro(null);
    setSucesso(false);
    if (!token) {
      return;
    }
    setEnviando(true);
    try {
      await apiClient.removerAssinatura(token);
      setAtual(null);
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }, [token]);

  return { atual, carregando, enviando, erro, sucesso, enviar, remover };
}
